import type {
  Me,
  RecentRef,
  RoleKey,
  SearchAward,
  SearchPerson,
  SearchResponse,
} from '@myrewards/contracts';
import { recheckRecent, searchPeopleAndAwards } from '../api/search';
import { STAGE_WORDS, statusOf } from '../awards/present';
import type { RecentFn, SearchFn, SearchResult, SearchResults } from '../shell/types';
import { formatMoney, formatShortDate } from '../ui/format';
import { matchPages, visiblePages, type SearchPage } from './pages';
import { forgetRecent, readRecent, type RecentEntry } from './recent';

/** How many people an award is for, in words ("1 person", "4 people"). */
function peopleWords(count: number): string {
  return count === 1 ? '1 person' : `${count} people`;
}

/** A person: their Admin record for directory staff, or Recognize with them for everyone else. */
export function personResult(person: SearchPerson): SearchResult {
  const meta = [person.sector?.name, person.jobTitle, person.left ? 'Left the company' : null]
    .filter((part): part is string => part !== null && part !== undefined && part !== '')
    .join(' · ');
  return {
    id: `person:${person.id}`,
    kind: 'person',
    label: person.name,
    ...(meta !== '' && { meta }),
    href:
      person.opens === 'admin_record'
        ? `/settings/system/people/${person.id}`
        : `/recognize?person=${encodeURIComponent(person.id)}`,
  };
}

/** An award: its record for staff and requesters, or the person's own award in My rewards. */
export function awardResult(award: SearchAward): SearchResult {
  if (award.opens === 'my_reward') {
    const stage = award.stage === null ? null : STAGE_WORDS[award.stage].label;
    const meta = [
      award.type === 'SPOT' ? 'Your Spot Award' : 'Your award',
      stage,
      award.chooseBy === null ? null : `choose by ${formatShortDate(award.chooseBy)}`,
    ]
      .filter((part): part is string => part !== null)
      .join(' · ');
    return {
      id: `reward:${award.id}`,
      kind: 'award',
      label: award.displayId ?? award.title ?? 'Your award',
      meta,
      href: `/my-rewards/${award.id}`,
    };
  }
  const meta = [
    statusOf(award).label,
    peopleWords(award.people),
    formatMoney(award.cashCents),
    award.title,
  ]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ');
  return {
    id: `award:${award.id}`,
    kind: 'award',
    label: award.displayId ?? award.title ?? 'Draft',
    meta,
    href: `/awards/${award.id}`,
  };
}

export function toResults(response: SearchResponse): Omit<SearchResults, 'pages'> {
  return {
    people: response.people.map(personResult),
    awards: response.awards.map(awardResult),
  };
}

type Lookup = (q: string, signal: AbortSignal) => Promise<SearchResponse>;

/**
 * The palette's search (APP-022): pages from the route tree, filtered by this person's roles, and
 * people and awards from the server, which only returns what they may already see.
 */
export function searchFor(
  me: Me,
  pages: readonly SearchPage[],
  lookup: Lookup = searchPeopleAndAwards,
): SearchFn {
  const mine = visiblePages(me, pages);
  return async (query, { signal }) => {
    const found = matchPages(mine, query);
    if (query.trim().length < 2) return { pages: found, people: [], awards: [] };
    return { pages: found, ...toResults(await lookup(query, signal)) };
  };
}

/**
 * Jump to's shortcuts for each role, most used first (SHELL1, APP-085). Only pages the route tree
 * lists for search, and only those the person can open, ever show.
 */
const JUMP_TO: Partial<Record<RoleKey, readonly string[]>> = {
  system_admin: [
    '/settings/system',
    '/settings/system/people',
    '/settings/system/audit-log',
    '/settings/system/go-live',
  ],
  // APP-234: the queue and its views under Manage.
  stock_admin: [
    '/manage/queue',
    '/manage/queue/mine',
    '/manage/queue/tax',
    '/manage/queue/in-progress',
    '/manage/queue/letters',
  ],
  payroll: ['/manage/payroll', '/settings/payroll'],
  executive: [
    '/manage/executive/budget-pools',
    '/insights',
    '/manage/executive/values',
    '/insights/ask',
  ],
  finance: ['/insights/spend', '/settings/budgets', '/insights/budgets'],
  sector_lead: ['/recognize', '/insights/team', '/awards', '/settings/sector'],
  // APP-112: a delegate enters awards and follows their own; Sector setup stays with the Lead.
  delegate: ['/recognize', '/awards'],
  requester: ['/recognize', '/awards'],
  manager: ['/letters-to-hand-out'],
  employee: [
    '/my-rewards',
    '/recognize/spot',
    '/my-rewards/elections',
    '/my-rewards/documents',
    '/settings/me',
    '/training',
  ],
};

/** Whose shortcuts come first when roles blend: the work roles, then everyone's own. */
const ROLE_ORDER: readonly RoleKey[] = [
  'system_admin',
  'stock_admin',
  'payroll',
  'executive',
  'finance',
  'sector_lead',
  'delegate',
  'requester',
  'manager',
  'employee',
];

const JUMP_TO_SHOWN = 6;

/**
 * What the palette offers before anything is typed, under Jump to: Home, then each of the
 * person's roles in turn, so a blended person gets some of each (R1). Never more than six.
 */
export function jumpToFor(me: Me, pages: readonly SearchPage[]): SearchResult[] {
  const visible = new Map(visiblePages(me, pages).map((page) => [page.href, page]));
  const held = new Set<RoleKey>(['employee', ...me.roles.map((r) => r.role)]);
  const lists = ROLE_ORDER.filter((role) => held.has(role)).map((role) => JUMP_TO[role] ?? []);
  const order = ['/'];
  for (let i = 0; lists.some((list) => i < list.length); i += 1) {
    for (const list of lists) {
      const href = list[i];
      if (href !== undefined) order.push(href);
    }
  }
  const picked: SearchResult[] = [];
  for (const href of new Set(order)) {
    const page = visible.get(href);
    if (page !== undefined) picked.push(page);
    if (picked.length === JUMP_TO_SHOWN) break;
  }
  return picked;
}

const RECENT_SHOWN = 5;

type RecentCheck = (refs: readonly RecentRef[], signal: AbortSignal) => Promise<SearchResponse>;

/** People and awards go to the server; pages are checked here. */
const isServerRef = (entry: RecentEntry): boolean => !entry.startsWith('page:');

/**
 * Recent (SHELL1, APP-085): this person's latest opens, re-checked each time the palette opens.
 * Pages go through the route tree's roles, people and awards through the server's scopes, and
 * anything they may no longer open is dropped, here and from the browser. Nothing while
 * previewing: Preview as never shows anyone's own history.
 */
export function recentFor(
  me: Me,
  pages: readonly SearchPage[],
  check: RecentCheck = recheckRecent,
): RecentFn | undefined {
  if (me.preview !== null) return undefined;
  const personId = me.person.id;
  const visible = visiblePages(me, pages);
  return async ({ signal }) => {
    const entries = readRecent(personId);
    if (entries.length === 0) return [];
    const refs = entries.filter(isServerRef);
    const found = refs.length === 0 ? { people: [], awards: [] } : await check(refs, signal);
    if (signal.aborted) return [];
    const results = new Map<string, SearchResult>(visible.map((page) => [page.id, page]));
    for (const person of found.people) results.set(`person:${person.id}`, personResult(person));
    for (const award of found.awards) {
      const result = awardResult(award);
      results.set(result.id, result);
    }
    forgetRecent(
      personId,
      entries.filter((entry) => !results.has(entry)),
    );
    return entries.flatMap((entry) => results.get(entry) ?? []).slice(0, RECENT_SHOWN);
  };
}
